import { useContext, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import styles from './styles.module.css'
import CircularProgress from '@mui/material/CircularProgress'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import ModalContent from './sub-modals/ModalContent'
import { fetchData } from '../../../global/utils/fetch'
import { createCase, getTechnicianAssignedToOrder, updateCase } from '../../api'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { ICase, ICasePartial } from '../../../../../app/entities/Case'
import hasPermission from '../../../global/utils/user/has-permission'
import { UserContext } from '../../../base/components/base-container/BaseContainer'
import { PERMISSIONS } from '../../../global/constants/permissions'
import { ITechnician } from '../../../../../app/entities/Technician'
import { IOrder } from '../../../../../app/entities/Order'

interface ICaseModalProps {
  isOpen: boolean
  handleClose: () => void
  refetch: () => void
  orderData: IOrder
  caseData: ICase | null
}

export function CasesModal({
  isOpen,
  handleClose,
  refetch,
  caseData,
  orderData,
}: ICaseModalProps) {
  const { user } = useContext(UserContext)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [isLoading, setIsLoading] = useState(false)
  const [technician, setTechnician] = useState<ITechnician | null>()
  const [caseFormData, setCaseFormData] = useState<ICasePartial>(caseData || {})

  const isCreate = !caseData?.objectId

  const hasRequiredFields = Boolean(
    caseFormData.type &&
      caseFormData.caseStatus &&
      caseFormData.summary?.trim() &&
      (!caseFormData.isRiskConnect ||
        caseFormData.riskConnectCaseNumber?.trim()),
  )

  const isEditDisabled =
    isLoading ||
    !hasRequiredFields ||
    !hasPermission(user).allow(
      PERMISSIONS.CASES.CATEGORY,
      PERMISSIONS.CASES.ENTRIES.EDIT_CASES.NAME,
    )

  function handleModalClose() {
    setCaseFormData({})
    setTechnician(null)
    handleClose()
  }

  async function handleCreateCase() {
    try {
      setIsLoading(true)
      await fetchData<ICase>(
        createCase(caseFormData, orderData.objectId, technician?.objectId),
      )
      refetch()
      handleModalClose()
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Successfully created case!',
        severity: 'success',
      })
    } catch (error) {
      console.error(error)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'There has been an error creating case.',
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  async function handleUpdateCase() {
    try {
      setIsLoading(true)
      await fetchData(updateCase(caseFormData as ICase, technician?.objectId))
      refetch()
      handleModalClose()
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Successfully updated case!',
        severity: 'success',
      })
    } catch (error) {
      console.error(error)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'There has been an error updating case.',
        severity: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return

    if (caseData) {
      setCaseFormData(caseData)
      setTechnician(caseData.technician || null)
    } else {
      async function getTechnicianAssignedToWorkOrder() {
        const assignedTechnician = await fetchData<ITechnician | null>(
          getTechnicianAssignedToOrder(orderData.objectId),
        )
        setTechnician(assignedTechnician)
      }
      setCaseFormData((prevState) => ({
        ...prevState,
        order: orderData,
      }))
      getTechnicianAssignedToWorkOrder()
    }
  }, [isOpen, caseData, orderData])

  return (
    <div>
      <Modal
        open={isOpen}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className={styles.casesModal}>
          <div className={styles.casesModalHeader}>
            <div className={`${styles.casesModalPageTitle} font--bold`}>
              {isCreate ? 'Create Case' : 'Edit Case'}
            </div>
          </div>
          {isLoading && (
            <div className={styles.progressContainer}>
              <CircularProgress />
            </div>
          )}
          <ModalContent
            technician={technician}
            setTechnician={setTechnician}
            orderData={orderData}
            caseFormData={caseFormData}
            setCaseFormData={setCaseFormData}
          />
          <div className={styles.casesModalFooterButtons}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <SecondaryButton
                buttonName='Cancel'
                onClick={handleModalClose}
              />
            </div>
            <div>
              <PrimaryButton
                buttonName={isCreate ? 'Create Case' : 'Save Case'}
                disabled={isEditDisabled}
                onClick={isCreate ? handleCreateCase : handleUpdateCase}
              />
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  )
}
